/**
 * FinalizationPage — Execution Workspace
 *
 * Thin orchestrator: loads data, handles finalize/hold/send-orders,
 * renders bucket tabs. All bucket content lives in ./finalization/
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Check, Loader2, Flag, Send } from 'lucide-react';
import { toast } from 'sonner';

// ── Bucket components (extracted from this file) ──────────────────────────────
import { CodingBucket }        from './finalization/CodingBucket';
import { PrescriptionsBucket } from './finalization/PrescriptionsBucket';
import { LabsBucket }          from './finalization/LabsBucket';
import { ImagingBucket }       from './finalization/ImagingBucket';
import { ActionsBucket }       from './finalization/ActionsBucket';
import { QualityReconciliationLane } from './finalization/QualityReconciliationLane';
import { ChartUpdatesBucket }  from './finalization/ChartUpdatesBucket';
import {
  FINALIZATION_BUCKETS,
  FinalizationSummary,
  ReconciliationPanel,
  getBucketCount,
  getBucketStatus,
} from './finalization/shared';

export default function FinalizationPage() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const [encounter, setEncounter] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState('prescriptions');
  const [finalizing, setFinalizing] = useState(false);
  const [qualityOverview, setQualityOverview] = useState(null);
  const [reconReport, setReconReport] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [showRecon, setShowRecon] = useState(false);
  const [acceptedUpdates, setAcceptedUpdates] = useState([]);
  const [priorEncounterCount, setPriorEncounterCount] = useState(0);
  const [holdLoading, setHoldLoading] = useState(false);
  const [sendOrdersLoading, setSendOrdersLoading] = useState(false);

  const fetchEncounter = useCallback(async () => {
    try {
      const res = await api.get(`/encounters/${encounterId}`);
      const enc = res.data.encounter;
      setEncounter(enc);
      if (enc.patient_id) {
        const [patRes, pastRes] = await Promise.all([
          api.get(`/patients/${enc.patient_id}`),
          api.get(`/encounters/patient/${enc.patient_id}`).catch(() => ({ data: { encounters: [] } })),
        ]);
        setPatient(patRes.data.patient);
        setPriorEncounterCount((pastRes.data.encounters || []).filter(e => e.id !== encounterId).length);
      }
      try {
        const qRes = await api.get(`/encounters/${encounterId}/quality-overview`);
        setQualityOverview(qRes.data);
      } catch {}
    } catch {
      toast.error('Failed to load encounter');
    } finally {
      setLoading(false);
    }
  }, [encounterId]);

  useEffect(() => { fetchEncounter(); }, [fetchEncounter]);

  const handleFinalize = async () => {
    setReconLoading(true);
    try {
      const reconRes = await api.post(`/encounters/${encounterId}/reconcile`);
      const report = reconRes.data;
      setReconReport(report);
      if (report.discrepancy_count > 0) {
        setShowRecon(true);
        setAcceptedUpdates((report.proposed_note_updates || []).map(u => u.id));
        setReconLoading(false);
        return;
      }
      setReconLoading(false);
      await doFinalize();
    } catch (err) {
      setReconLoading(false);
      toast.error('Reconciliation failed: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleApplyAndFinalize = async () => {
    setFinalizing(true);
    try {
      if (reconReport && acceptedUpdates.length > 0) {
        await api.post(`/encounters/${encounterId}/reconcile/apply`, {
          report_id: reconReport.id,
          accepted_updates: acceptedUpdates,
        });
      }
      await doFinalize();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finalization failed');
      setFinalizing(false);
    }
  };

  const handleSkipAndFinalize = async () => {
    setFinalizing(true);
    try { await doFinalize(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Finalization failed'); setFinalizing(false); }
  };

  const doFinalize = async () => {
    setFinalizing(true);
    try {
      // Auto-apply any remaining pending problem signals before navigating away.
      // This ensures chart updates happen even if the provider skipped the Chart Updates tab.
      // Duplicates are handled gracefully server-side (409s are silently skipped).
      try {
        const sigRes = await api.get(`/encounters/${encounterId}/chart-update-preview`);
        const pendingSignals = (sigRes.data.problem_signals || []).filter(s => s.status === 'pending');
        if (pendingSignals.length > 0) {
          await api.post(`/encounters/${encounterId}/chart-update-preview/apply`, {
            approved_signal_ids: pendingSignals.map(s => s.id),
          });
        }
      } catch {} // Non-blocking — chart updates must never block finalization

      // Auto-apply quality signals (BP readings, orders placed, completions reported)
      // This persists the encounter quality evidence to the patient's longitudinal chart.
      try {
        await api.post(`/encounters/${encounterId}/quality-signals/finalize-apply`);
      } catch {} // Non-blocking

      const res = await api.post(`/command-center/${encounterId}/finalize`);
      setEncounter(res.data.encounter);

      // Sprint A.5 — Quality success toast
      try {
        const chipRes = await api.get(`/encounters/${encounterId}/quality-chip-state`);
        const s = chipRes.data.summary || {};
        const parts = [];
        if (s.compliant > 0) parts.push(`${s.compliant} measure${s.compliant !== 1 ? 's' : ''} captured`);
        if (s.due > 0) parts.push(`${s.due} outstanding`);
        if (parts.length > 0) {
          toast.success(`Quality: ${parts.join(' · ')}`, { duration: s.due > 0 ? 4000 : 2000 });
        }
      } catch {}

      toast.success('Encounter finalized');
      navigate(`/note/${encounterId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finalization failed');
    } finally { setFinalizing(false); }
  };

  const handleHold = async () => {
    setHoldLoading(true);
    try {
      await api.post(`/encounters/${encounterId}/hold`, { reason: 'Provider flagged for follow-up' });
      toast.success('Encounter flagged — stays in your queue until finalized');
      navigate('/my-day');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to flag encounter');
    } finally { setHoldLoading(false); }
  };

  const handleSendOrders = async () => {
    setSendOrdersLoading(true);
    try {
      const res = await api.post(`/encounters/${encounterId}/send-orders`);
      const { queued_count = 0 } = res.data;
      toast.success(`Orders sent${queued_count > 0 ? ` — ${queued_count} prescription(s) added to Refill queue` : ''}. Note still open.`);
      fetchEncounter();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send orders');
    } finally { setSendOrdersLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!encounter) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Encounter not found</p>
      </div>
    );
  }

  const enc = encounter;
  const outputs = enc.encounter_outputs || {};

  return (
    <div className="flex flex-col h-full" data-testid="finalization-page">
      {/* Header */}
      <div className="px-5 py-3 border-b bg-card flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs"
          onClick={() => navigate(`/visit/${encounterId}`)} data-testid="finalize-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Visit
        </Button>
        <div className="h-5 w-px bg-border" />
        {patient && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">
              {patient.name?.first?.[0]}{patient.name?.last?.[0]}
            </div>
            <div>
              <span className="text-sm font-medium">{patient.name?.first} {patient.name?.last}</span>
              <Badge variant="outline" className="text-[9px] ml-2 font-mono">{patient.mrn}</Badge>
            </div>
          </div>
        )}
        <div className="flex-1" />
        <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Finalization Workspace
        </h2>
      </div>

      {/* Bucket Tabs */}
      <div className="px-5 py-2 border-b bg-muted/20 flex gap-1.5 shrink-0 overflow-x-auto" data-testid="finalize-bucket-tabs"
        onKeyDown={(e) => {
          const keys = FINALIZATION_BUCKETS.map(b => b.key);
          const idx = keys.indexOf(activeBucket);
          if (e.key === 'ArrowRight' && idx < keys.length - 1) { setActiveBucket(keys[idx + 1]); e.preventDefault(); }
          if (e.key === 'ArrowLeft' && idx > 0) { setActiveBucket(keys[idx - 1]); e.preventDefault(); }
        }}
        tabIndex={0}
      >
        {FINALIZATION_BUCKETS.map(b => {
          const isActive = activeBucket === b.key;
          const Icon = b.icon;
          const count = getBucketCount(b.key, enc, outputs, qualityOverview);
          const status = getBucketStatus(b.key, enc, outputs, qualityOverview);
          return (
            <button
              key={b.key}
              onClick={() => setActiveBucket(b.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all whitespace-nowrap ${isActive ? 'bg-foreground text-background shadow-sm' : 'bg-card border border-border hover:border-foreground/20'}`}
              data-testid={`finalize-tab-${b.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {b.label}
              {count > 0 && (
                <span className={`text-[9px] rounded-full px-1.5 h-4 flex items-center justify-center ${isActive ? 'bg-background/20' : 'bg-muted'}`}>
                  {count}
                </span>
              )}
              {status === 'complete' && !isActive && <Check className="w-3 h-3 text-emerald-500" />}
              {status === 'pending' && !isActive && count > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
            </button>
          );
        })}
      </div>

      {/* Active Bucket */}
      <ScrollArea className="flex-1">
        <div className="p-5 max-w-4xl">
          {activeBucket === 'coding' && (
            <CodingBucket enc={enc} outputs={outputs} encounterId={encounterId}
              fetchEncounter={fetchEncounter} setEncounter={setEncounter}
              patient={patient} priorEncounterCount={priorEncounterCount} />
          )}
          {activeBucket === 'prescriptions' && (
            <PrescriptionsBucket enc={enc} outputs={outputs} encounterId={encounterId}
              fetchEncounter={fetchEncounter} setEncounter={setEncounter} />
          )}
          {activeBucket === 'labs' && (
            <LabsBucket enc={enc} encounterId={encounterId} fetchEncounter={fetchEncounter} setEncounter={setEncounter} outputs={outputs} />
          )}
          {activeBucket === 'imaging' && (
            <ImagingBucket enc={enc} encounterId={encounterId} fetchEncounter={fetchEncounter} outputs={outputs} />
          )}
          {activeBucket === 'actions' && (
            <ActionsBucket enc={enc} encounterId={encounterId} fetchEncounter={fetchEncounter} outputs={outputs} />
          )}
          {activeBucket === 'chart_updates' && (
            <ChartUpdatesBucket enc={enc} encounterId={encounterId} fetchEncounter={fetchEncounter} />
          )}
          {activeBucket === 'aco' && (
            <QualityReconciliationLane enc={enc} outputs={outputs} encounterId={encounterId}
              fetchEncounter={fetchEncounter} qualityOverview={qualityOverview} />
          )}
        </div>
      </ScrollArea>

      {/* Reconciliation Panel */}
      {showRecon && reconReport && (
        <ReconciliationPanel
          report={reconReport}
          acceptedUpdates={acceptedUpdates}
          setAcceptedUpdates={setAcceptedUpdates}
          onApplyAndFinalize={handleApplyAndFinalize}
          onSkipAndFinalize={handleSkipAndFinalize}
          onCancel={() => setShowRecon(false)}
          finalizing={finalizing}
        />
      )}

      {/* Bottom Action Bar */}
      {!showRecon && (
        <div className="px-5 py-3 border-t bg-card flex items-center justify-between shrink-0 gap-2" data-testid="finalize-action-bar">
          <FinalizationSummary enc={enc} outputs={outputs} qualityOverview={qualityOverview} />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={handleHold} disabled={holdLoading || enc.needs_followup} data-testid="hold-for-followup-btn">
              {holdLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
              {enc.needs_followup ? 'Flagged' : 'Flag Follow-Up'}
            </Button>
            <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50"
              onClick={handleSendOrders} disabled={sendOrdersLoading} data-testid="send-orders-continue-btn">
              {sendOrdersLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send Orders, Continue Later
            </Button>
            <Button size="sm" className="h-9 px-6 text-xs gap-1.5"
              onClick={handleFinalize} disabled={finalizing || reconLoading} data-testid="finalize-sign-btn">
              {(finalizing || reconLoading) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {reconLoading ? 'Reconciling...' : finalizing ? 'Finalizing...' : 'Sign & Finalize'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
